const BPS = 10_000n
const SCALE = 1_000_000_000_000_000_000n

/// Mirrors HorizonLendingPool's own (removed) `_utilizationBps`/`utilizationBps` - neither had any
/// internal caller beyond the accrual math that stays on-chain (`debtOf`/`supplyBalanceOf` use
/// the still-present `_utilizationBps` internally, unchanged); this is purely the frontend-display
/// copy, computed client-side instead of moved to a peripheral contract - see HorizonLendingPool.sol's
/// own doc comment at the removal site. `liquidBalanceRaw` is the reserve's actual liquid balance
/// (native ETH balance of the pool, or `token.balanceOf(pool)`) in the asset's own decimals.
export function computeUtilizationBps(totalBorrowsWei, liquidBalanceRaw, decimals) {
  if (totalBorrowsWei === 0n) return 0n
  const cash = decimals === 18 ? liquidBalanceRaw : liquidBalanceRaw * (10n ** BigInt(18 - decimals))
  return (totalBorrowsWei * BPS) / (cash + totalBorrowsWei)
}

/// Mirrors HorizonLendingPool's own (removed) `_borrowRateBps`/`borrowRateBps`.
export function computeBorrowRateBps(utilBps, baseRateBps, slope1Bps, slope2Bps, kinkBps) {
  const base = BigInt(baseRateBps), s1 = BigInt(slope1Bps), s2 = BigInt(slope2Bps), kink = BigInt(kinkBps)
  if (kink === 0n) return base
  if (utilBps <= kink) return base + (s1 * utilBps) / kink
  const excessUtil = utilBps - kink
  return base + s1 + (s2 * excessUtil) / (BPS - kink)
}

/// Mirrors HorizonLendingPool's own (removed) `supplyRateBps`.
export function computeSupplyRateBps(utilBps, borrowRateBps, reserveFactorBps) {
  return (borrowRateBps * utilBps * (BPS - BigInt(reserveFactorBps))) / (BPS * BPS)
}

// ─── Adaptive IRM (Morpho AdaptiveCurveIRM port) ──────────────────────────────────────────────
// The INSTANTANEOUS half of contracts/libraries/AdaptiveIrmMath.sol - i.e. `_curve(rateAtTarget,
// err)` annualized. That is exactly what a reserve on adaptive IRM charges right now at a given
// utilization: `rateAtTarget` (its stored per-second WAD rate at the 90% target) scaled by a
// piecewise-linear function of how far utilization sits from target - gentle below, 4x steep above.
// We deliberately do NOT re-derive the TIME adaptation (wExp / rate-at-target drift) here: that is
// what moves the curve between blocks, and the frontend reads the CURRENT rateAtTarget straight off
// `reserveState`, so the instantaneous curve is all the display needs. Kept bit-for-bit with the
// Solidity constants so the plotted curve cannot disagree with the chain.
const A_TARGET = 900_000_000_000_000_000n           // 0.9 WAD target utilization
const A_STEEP = 4_000_000_000_000_000_000n          // 4 WAD curve steepness
const A_YEAR = 31_536_000n                          // 365 days, seconds
export const ADAPTIVE_TARGET_BPS = 9_000            // 90%, for the chart's pivot marker
// INITIAL_RATE_AT_TARGET = 0.04/yr as a per-second WAD int (0.04e18 / 365 days), the seed a reserve
// uses on its first adaptive interval - shown when rateAtTarget has not been persisted yet.
const A_INITIAL_RAT = 40_000_000_000_000_000n / A_YEAR

/// Instantaneous adaptive borrow rate in per-year bps at `utilBps`, given the reserve's stored
/// per-second-WAD `rateAtTargetPerSec` (int, from reserveState). `<= 0` means "not accrued on
/// adaptive yet" -> the contract's initial 4%/yr seed.
export function computeAdaptiveBorrowRateBps(utilBps, rateAtTargetPerSec) {
  let rat = BigInt(rateAtTargetPerSec ?? 0n)
  if (rat <= 0n) rat = A_INITIAL_RAT
  const u = (BigInt(utilBps) * SCALE) / BPS                                   // WAD utilization
  const norm = u > A_TARGET ? (SCALE - A_TARGET) : A_TARGET
  const err = ((u - A_TARGET) * SCALE) / norm                                 // signed, [-1,1] WAD
  const coeff = err < 0n ? (SCALE - (SCALE * SCALE) / A_STEEP) : (A_STEEP - SCALE)
  const instPerSec = (((coeff * err) / SCALE + SCALE) * rat) / SCALE          // _curve, per-second WAD
  return (instPerSec * A_YEAR * BPS) / SCALE                                  // -> per-year bps
}

/// The reserve's current rate-at-target itself, annualized to bps (the borrow rate AT 90% util) -
/// for the "Rate at target" readout on an adaptive reserve.
export function adaptiveRateAtTargetBps(rateAtTargetPerSec) {
  let rat = BigInt(rateAtTargetPerSec ?? 0n)
  if (rat <= 0n) rat = A_INITIAL_RAT
  return (rat * A_YEAR * BPS) / SCALE
}

/// Mirrors HorizonLendingPool's own (removed) `accountData` - collateral leg weighted by each
/// reserve's `collateralFactorBps`, debt leg unweighted, both from each reserve's own `priceOf`
/// read (a real on-chain call that still reverts on a stale/broken feed - the same tolerance the
/// removed `_priceOrZeroIfStale`/strict `priceOf` split had on-chain collapses here to "a stale
/// reserve just contributes 0", the conservative/undercount-only direction either fallback took).
/// Display-only - no funds move off this number; the pool's own `healthFactor()`/
/// `_isHealthyForAction` (unchanged, still on-chain) are what actually gate borrows/withdrawals/
/// liquidations. `positions` is an array of `{ supplyBalance, debt, priceUsd, collateralFactorBps }`,
/// all already-converted JS numbers (not wei) - one entry per reserve.
export function computeAccountUsd(positions) {
  let totalCollateralUsd = 0
  let totalDebtUsd = 0
  for (const { supplyBalance, debt, priceUsd, collateralFactorBps } of positions) {
    totalCollateralUsd += (supplyBalance ?? 0) * (priceUsd ?? 0) * ((collateralFactorBps ?? 0) / 10_000)
    totalDebtUsd += (debt ?? 0) * (priceUsd ?? 0)
  }
  const availableBorrowsUsd = totalCollateralUsd > totalDebtUsd ? totalCollateralUsd - totalDebtUsd : 0
  return { totalCollateralUsd, totalDebtUsd, availableBorrowsUsd }
}
